import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as adminService from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';
import './AdminUsersPage.css';

type BulkAction = adminService.BulkUserAction;

interface UserRow {
  id: string;
  username: string;
  email: string;
  avatarUrl?: string;
  role: string;
  subscriptionStatus: string;
  status: string;
  accountType?: string;
  tokenBalance: number;
  createdAt: string;
}

interface UsersResponse {
  data: UserRow[];
  total: number;
  page: number;
  limit: number;
}

const AdminUsersPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  const [users, setUsers] = useState<UserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<BulkAction | ''>('');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkAccountType, setBulkAccountType] = useState<string>('comp');
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState<adminService.BulkUsersResult | null>(null);

  // Filters
  const [roleFilter, setRoleFilter] = useState('all');
  const [subFilter, setSubFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Summary
  const [summary, setSummary] = useState({ totalUsers: 0, activeSubscribers: 0, admins: 0 });

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = { page, limit };
      if (roleFilter !== 'all') params.role = roleFilter;
      if (subFilter !== 'all') params.subscriptionStatus = subFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (accountTypeFilter !== 'all') params.accountType = accountTypeFilter;
      if (debouncedSearch) params.search = debouncedSearch;

      const res: UsersResponse = await adminService.getUsers(params);
      setUsers(res.data);
      setTotal(res.total);

      // Compute summary from unfiltered data if first load
      if (roleFilter === 'all' && subFilter === 'all' && statusFilter === 'all' && accountTypeFilter === 'all' && !debouncedSearch) {
        setSummary({
          totalUsers: res.total,
          activeSubscribers: res.data.filter((u: UserRow) => u.subscriptionStatus === 'active').length,
          admins: res.data.filter((u: UserRow) => u.role === 'admin').length,
        });
      }
    } catch {
      setError(t('users.fetchError', 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  }, [page, limit, roleFilter, subFilter, statusFilter, accountTypeFilter, debouncedSearch, t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, roleFilter, subFilter, statusFilter, accountTypeFilter, debouncedSearch]);

  const totalPages = Math.ceil(total / limit);

  const pageIds = useMemo(() => users.map((u) => u.id), [users]);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const someOnPageSelected = pageIds.some((id) => selectedIds.has(id));

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const openBulkConfirm = (action: BulkAction) => {
    setBulkAction(action);
    setBulkReason('');
    setBulkAccountType('comp');
    setBulkResult(null);
    setBulkConfirmOpen(true);
  };

  const bulkReasonRequired = bulkAction === 'suspend' || bulkAction === 'changeAccountType';
  const bulkCanSubmit =
    !!bulkAction &&
    selectedIds.size > 0 &&
    (!bulkReasonRequired || bulkReason.trim().length >= 3);

  const submitBulk = async () => {
    if (!bulkAction || selectedIds.size === 0) return;
    setBulkSubmitting(true);
    try {
      const payload: adminService.BulkUsersPayload = {
        userIds: Array.from(selectedIds),
        action: bulkAction,
      };
      if (bulkReason.trim()) payload.reason = bulkReason.trim();
      if (bulkAction === 'changeAccountType') payload.accountType = bulkAccountType;
      const res = await adminService.bulkUpdateUsers(payload);
      setBulkResult(res);
      await fetchUsers();
      if (res.failed === 0) {
        setSelectedIds(new Set());
      } else {
        // keep only failed rows selected so the admin can retry
        const failedIds = new Set(res.results.filter((r) => !r.ok).map((r) => r.userId));
        setSelectedIds(failedIds);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setBulkResult({
        total: selectedIds.size,
        succeeded: 0,
        failed: selectedIds.size,
        results: Array.from(selectedIds).map((id) => ({ userId: id, ok: false, error: message })),
      });
    } finally {
      setBulkSubmitting(false);
    }
  };

  const closeBulkConfirm = () => {
    if (bulkSubmitting) return;
    setBulkConfirmOpen(false);
    setBulkAction('');
    setBulkReason('');
    setBulkResult(null);
  };

  const formatDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getInitial = (name: string): string => {
    return (name || '?').charAt(0).toUpperCase();
  };

  const getRoleBadgeClass = (role: string): string => {
    if (role === 'admin') return 'admin-user-badge admin-user-badge--admin';
    if (role === 'support') return 'admin-user-badge admin-user-badge--support';
    if (role === 'creator') return 'admin-user-badge admin-user-badge--creator';
    return 'admin-user-badge admin-user-badge--user';
  };

  const getSubBadgeClass = (status: string): string => {
    if (status === 'active') return 'admin-user-badge admin-user-badge--active';
    if (status === 'trialing') return 'admin-user-badge admin-user-badge--trialing';
    if (status === 'cancelled') return 'admin-user-badge admin-user-badge--cancelled';
    return 'admin-user-badge admin-user-badge--inactive';
  };

  const getAccountTypeBadge = (type?: string): { cls: string; label: string } | null => {
    if (!type || type === 'regular') return null;
    if (type === 'internal') return { cls: 'admin-user-badge admin-user-badge--internal', label: 'Internal' };
    if (type === 'comp') return { cls: 'admin-user-badge admin-user-badge--comp', label: 'Comp' };
    if (type === 'beta_tester') return { cls: 'admin-user-badge admin-user-badge--beta', label: 'Beta' };
    return { cls: 'admin-user-badge admin-user-badge--internal', label: type };
  };

  const renderSkeleton = () => {
    const cols = isAdmin ? 7 : 6;
    return (
      <div className="admin-user-table-wrap">
        <table className="admin-user-table">
          <thead>
            <tr>
              {Array.from({ length: cols }).map((_, i) => (
                <th key={i}><div className="admin-user-skeleton-cell" style={{ width: i === 0 ? 180 : 80 }} /></th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 8 }).map((_, i) => (
              <tr key={i} className="admin-user-skeleton-row">
                {Array.from({ length: cols }).map((_, j) => (
                  <td key={j}><div className="admin-user-skeleton-cell" /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (error) {
    return (
      <div className="admin-user-page">
        <h1 className="admin-user-title">{t('users.title', 'User Management')}</h1>
        <div className="admin-user-error">
          <div className="admin-user-error-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <p className="admin-user-error-text">{error}</p>
          <button className="admin-user-retry-btn" onClick={fetchUsers}>
            {t('common.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-user-page">
      <h1 className="admin-user-title">{t('users.title', 'User Management')}</h1>

      {/* Summary Cards */}
      <div className="admin-user-summary">
        <div className="admin-user-summary-card">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <div className="admin-user-summary-data">
            <span className="admin-user-summary-value">{summary.totalUsers.toLocaleString()}</span>
            <span className="admin-user-summary-label">{t('users.totalUsers', 'Total Users')}</span>
          </div>
        </div>
        <div className="admin-user-summary-card">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          <div className="admin-user-summary-data">
            <span className="admin-user-summary-value">{summary.activeSubscribers.toLocaleString()}</span>
            <span className="admin-user-summary-label">{t('users.activeSubscribers', 'Active Subscribers')}</span>
          </div>
        </div>
        <div className="admin-user-summary-card">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#764ba2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <div className="admin-user-summary-data">
            <span className="admin-user-summary-value">{summary.admins.toLocaleString()}</span>
            <span className="admin-user-summary-label">{t('users.admins', 'Admins')}</span>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="admin-user-filters">
        <div className="admin-user-filter-group">
          <label className="admin-user-filter-label">{t('users.role', 'Role')}</label>
          <select
            className="admin-user-select"
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
          >
            <option value="all">{t('users.all', 'All')}</option>
            <option value="user">{t('users.roleUser', 'User')}</option>
            <option value="support">{t('users.roleSupport', 'Support')}</option>
            <option value="admin">{t('users.roleAdmin', 'Admin')}</option>
          </select>
        </div>
        <div className="admin-user-filter-group">
          <label className="admin-user-filter-label">{t('users.subscription', 'Subscription')}</label>
          <select
            className="admin-user-select"
            value={subFilter}
            onChange={(e) => { setSubFilter(e.target.value); setPage(1); }}
          >
            <option value="all">{t('users.all', 'All')}</option>
            <option value="active">{t('users.subActive', 'Active')}</option>
            <option value="trialing">{t('users.subTrialing', 'Trialing')}</option>
            <option value="cancelled">{t('users.subCancelled', 'Cancelled')}</option>
            <option value="inactive">{t('users.subInactive', 'Inactive')}</option>
          </select>
        </div>
        <div className="admin-user-filter-group">
          <label className="admin-user-filter-label">{t('users.accountStatus', 'Account Status')}</label>
          <select
            className="admin-user-select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="all">{t('users.all', 'All')}</option>
            <option value="active">{t('users.statusActive', 'Active')}</option>
            <option value="suspended">{t('users.statusSuspended', 'Suspended')}</option>
          </select>
        </div>
        <div className="admin-user-filter-group">
          <label className="admin-user-filter-label">{t('users.accountType', 'Account Type')}</label>
          <select
            className="admin-user-select"
            value={accountTypeFilter}
            onChange={(e) => { setAccountTypeFilter(e.target.value); setPage(1); }}
          >
            <option value="all">{t('users.all', 'All')}</option>
            <option value="regular">{t('users.accountTypeRegular', 'Regular')}</option>
            <option value="internal">{t('users.accountTypeInternal', 'Internal')}</option>
            <option value="comp">{t('users.accountTypeComp', 'Comp')}</option>
            <option value="beta_tester">{t('users.accountTypeBeta', 'Beta Tester')}</option>
          </select>
        </div>
        <div className="admin-user-filter-group admin-user-filter-group--search">
          <label className="admin-user-filter-label">{t('users.search', 'Search')}</label>
          <div className="admin-user-search-box">
            <svg className="admin-user-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              className="admin-user-search-input"
              type="text"
              placeholder={t('users.searchPlaceholder', 'Email or username...')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {isAdmin && selectedIds.size > 0 && (
        <div className="admin-user-bulk-bar" role="region" aria-label={t('users.bulkBar', 'Bulk actions')}>
          <div className="admin-user-bulk-left">
            <span className="admin-user-bulk-count">
              {t('users.bulkSelected', '{{count}} selected', { count: selectedIds.size })}
            </span>
            <button className="admin-user-bulk-clear" type="button" onClick={clearSelection}>
              {t('users.bulkClear', 'Clear')}
            </button>
          </div>
          <div className="admin-user-bulk-actions">
            <button
              className="admin-user-bulk-btn admin-user-bulk-btn--warn"
              type="button"
              onClick={() => openBulkConfirm('suspend')}
            >
              {t('users.bulkSuspend', 'Suspend')}
            </button>
            <button
              className="admin-user-bulk-btn"
              type="button"
              onClick={() => openBulkConfirm('activate')}
            >
              {t('users.bulkActivate', 'Activate')}
            </button>
            <button
              className="admin-user-bulk-btn"
              type="button"
              onClick={() => openBulkConfirm('changeAccountType')}
            >
              {t('users.bulkChangeAccountType', 'Change Account Type')}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? renderSkeleton() : users.length === 0 ? (
        <div className="admin-user-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
          </svg>
          <p>{t('users.noUsers', 'No users found matching your filters.')}</p>
        </div>
      ) : (
        <div className="admin-user-table-wrap">
          <table className="admin-user-table">
            <thead>
              <tr>
                {isAdmin && (
                  <th className="admin-user-checkbox-col">
                    <input
                      type="checkbox"
                      aria-label={t('users.selectAllOnPage', 'Select all on page')}
                      checked={allOnPageSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected;
                      }}
                      onChange={toggleAllOnPage}
                    />
                  </th>
                )}
                <th>{t('users.colUser', 'User')}</th>
                <th>{t('users.colRole', 'Role')}</th>
                <th>{t('users.colSubscription', 'Subscription')}</th>
                <th>{t('users.colTokens', 'Token Balance')}</th>
                <th>{t('users.colJoined', 'Joined')}</th>
                <th>{t('users.colActions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const acctBadge = getAccountTypeBadge(user.accountType);
                const checked = selectedIds.has(user.id);
                return (
                <tr key={user.id} className={checked ? 'admin-user-row--selected' : undefined}>
                  {isAdmin && (
                    <td className="admin-user-checkbox-col">
                      <input
                        type="checkbox"
                        aria-label={t('users.selectUser', 'Select user {{name}}', { name: user.username })}
                        checked={checked}
                        onChange={() => toggleRow(user.id)}
                      />
                    </td>
                  )}
                  <td>
                    <div className="admin-user-cell-user">
                      <div className="admin-user-avatar">
                        {user.avatarUrl ? (
                          <img src={user.avatarUrl} alt={user.username} />
                        ) : (
                          <span>{getInitial(user.username)}</span>
                        )}
                      </div>
                      <div className="admin-user-cell-info">
                        <span className="admin-user-cell-name">
                          {user.username}
                          {acctBadge && (
                            <span className={acctBadge.cls} style={{ marginLeft: 8 }}>{acctBadge.label}</span>
                          )}
                        </span>
                        <span className="admin-user-cell-email">{user.email}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={getRoleBadgeClass(user.role)}>{user.role}</span>
                  </td>
                  <td>
                    <span className={getSubBadgeClass(user.subscriptionStatus)}>
                      {user.subscriptionStatus || 'inactive'}
                    </span>
                  </td>
                  <td className="admin-user-cell-number">{user.tokenBalance?.toLocaleString() ?? 0}</td>
                  <td className="admin-user-cell-date">{formatDate(user.createdAt)}</td>
                  <td>
                    <button
                      className="admin-user-view-btn"
                      onClick={() => navigate(`/admin/users/${user.id}`)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      {t('users.view', 'View')}
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="admin-user-pagination">
          <button
            className="admin-user-page-btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {t('common.prev', 'Previous')}
          </button>
          <span className="admin-user-page-info">
            {t('users.pageInfo', 'Page {{page}} of {{total}}', { page, total: totalPages })}
          </span>
          <button
            className="admin-user-page-btn"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            {t('common.next', 'Next')}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}

      {/* Bulk action confirmation modal */}
      {bulkConfirmOpen && (
        <div className="admin-user-modal-backdrop" role="dialog" aria-modal="true">
          <div className="admin-user-modal">
            <h2 className="admin-user-modal-title">
              {bulkAction === 'suspend' && t('users.bulkConfirmSuspendTitle', 'Suspend {{count}} users', { count: selectedIds.size })}
              {bulkAction === 'activate' && t('users.bulkConfirmActivateTitle', 'Activate {{count}} users', { count: selectedIds.size })}
              {bulkAction === 'changeAccountType' && t('users.bulkConfirmAccountTypeTitle', 'Change account type for {{count}} users', { count: selectedIds.size })}
            </h2>

            {!bulkResult && (
              <>
                {bulkAction === 'changeAccountType' && (
                  <div className="admin-user-modal-field">
                    <label className="admin-user-modal-label">{t('users.bulkAccountType', 'New account type')}</label>
                    <select
                      className="admin-user-select"
                      value={bulkAccountType}
                      onChange={(e) => setBulkAccountType(e.target.value)}
                    >
                      <option value="regular">{t('users.accountTypeRegular', 'Regular')}</option>
                      <option value="internal">{t('users.accountTypeInternal', 'Internal')}</option>
                      <option value="comp">{t('users.accountTypeComp', 'Comp')}</option>
                      <option value="beta_tester">{t('users.accountTypeBeta', 'Beta Tester')}</option>
                    </select>
                  </div>
                )}

                <div className="admin-user-modal-field">
                  <label className="admin-user-modal-label">
                    {t('users.bulkReason', 'Reason')}
                    {bulkReasonRequired && <span className="admin-user-required"> *</span>}
                  </label>
                  <textarea
                    className="admin-user-modal-textarea"
                    rows={3}
                    value={bulkReason}
                    onChange={(e) => setBulkReason(e.target.value)}
                    placeholder={bulkReasonRequired
                      ? t('users.bulkReasonRequired', 'Required — minimum 3 characters')
                      : t('users.bulkReasonOptional', 'Optional')}
                  />
                </div>

                <p className="admin-user-modal-note">
                  {t('users.bulkAuditNote', 'This action will be logged in the audit trail with your admin ID.')}
                </p>

                <div className="admin-user-modal-actions">
                  <button
                    className="admin-user-modal-btn"
                    type="button"
                    onClick={closeBulkConfirm}
                    disabled={bulkSubmitting}
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button
                    className="admin-user-modal-btn admin-user-modal-btn--primary"
                    type="button"
                    onClick={submitBulk}
                    disabled={!bulkCanSubmit || bulkSubmitting}
                  >
                    {bulkSubmitting
                      ? t('users.bulkSubmitting', 'Applying...')
                      : t('users.bulkApply', 'Apply to {{count}} users', { count: selectedIds.size })}
                  </button>
                </div>
              </>
            )}

            {bulkResult && (
              <>
                <div className="admin-user-modal-result">
                  <div className="admin-user-modal-result-row">
                    <span>{t('users.bulkResultSucceeded', 'Succeeded')}</span>
                    <strong className="admin-user-result-ok">{bulkResult.succeeded}</strong>
                  </div>
                  <div className="admin-user-modal-result-row">
                    <span>{t('users.bulkResultFailed', 'Failed')}</span>
                    <strong className={bulkResult.failed > 0 ? 'admin-user-result-fail' : ''}>{bulkResult.failed}</strong>
                  </div>
                </div>

                {bulkResult.failed > 0 && (
                  <details className="admin-user-modal-details">
                    <summary>{t('users.bulkShowFailures', 'Show failures')}</summary>
                    <ul className="admin-user-modal-failures">
                      {bulkResult.results.filter((r) => !r.ok).map((r) => (
                        <li key={r.userId}>
                          <code>{r.userId}</code>: {r.error ?? t('common.error', 'Error')}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                <div className="admin-user-modal-actions">
                  <button
                    className="admin-user-modal-btn admin-user-modal-btn--primary"
                    type="button"
                    onClick={closeBulkConfirm}
                  >
                    {t('common.close', 'Close')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
